import React, { useState, useEffect } from 'react';
import {
    Button,
    Form,
    Input,
    NavBar,
    Toast,
    Picker,
    Checkbox,
    Steps,
    SpinLoading
} from 'antd-mobile';
import {getNavBarContainerStyle} from "../../../../utils/deviceUtils";
import type { ImageUploadItem } from 'antd-mobile/es/components/image-uploader';
import type { PickerValue } from 'antd-mobile/es/components/picker';
import { useNavigate } from 'react-router-dom';
import { 
    EyeOutline, 
    EyeInvisibleOutline,
    CloseOutline,
    AddOutline,
    UserOutline,
    CameraOutline
} from 'antd-mobile-icons';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import {
    CREATE_ACCOUNT_DELIVERY_DRIVER_MUTATION,
    CHECK_DSP_CODE_EXISTS,
    CreateAccountDeliveryDriverInput,
    CreateAccountDeliveryDriverResponse,
    CreateAccountDeliveryDriverVariables
} from '../../../../graphql/mutations/createAccountDeliveryDriver';
import { GET_ALL_VEHICLE_TYPES, GetAllVehicleTypesResponse
} from '../../../../graphql/mutations/vehicleType';
import { checkPassword } from '../../../../utils/passwordValidation';
import { registrationFileUpload } from '../../../../utils/storageUtils';
import PhotoSelector from '../../components/PhotoSelector';
import {t} from "../../../../i18n";
import {useTranslation} from "react-i18next";
import { useSendVerificationCode } from '../../../../hooks/useSendVerificationCode';
import { validatePhoneNumber } from '../../../../utils/phoneValidation';
import { validateVerificationCode } from '../../../../utils/verificationCodeValidation';

const { Step } = Steps;

const countryCodes = [
    { label: '+1', value: '+1', flag: '🇺🇸' },
];

const UnifiedRegister: React.FC = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const { i18n } = useTranslation();
    
    // 统一的表单数据状态管理
    const [formData, setFormData] = useState({
        dspCode: '',
        email: '',
        phone: '',
        countryCode: '+1',
        password: '',
        confirm: '',
        firstName: '',
        middleName: '',
        lastName: '',
        vehiclePlate: '',
        vehicleTypeId: '0',
        verificationCode: ''
    });
    
    // Step 2 states
    const [pickerVisible, setPickerVisible] = useState(false);
    const [selectedCountryCode, setSelectedCountryCode] = useState<string>('+1');
    const [verificationCode, setVerificationCode] = useState('');
    const { sendCode: sendRegisterVerificationCode, countdown } = useSendVerificationCode({
        type: 'register',
        getPhone: () => ({
            phone: formData.phone,
            countryCode: selectedCountryCode
        }),
        onAccountExists: () => {
            const message = t('errors.business.account.register.phoneExists');
            Toast.show(message);
        }
    });
    
    // Step 3 states
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
    // Step 4 states
    const [avatarFileList, setAvatarFileList] = useState<ImageUploadItem[]>([]);
    const [avatarUploadInfo, setAvatarUploadInfo] = useState<UploadInfo | null>(null);
    // 手机号验证码校验通过时服务端下发的一次性 token，上传照片与提交注册时带上
    const [registrationToken, setRegistrationToken] = useState<string | null>(null);
    const [avatarUploading, setAvatarUploading] = useState(false);
    const [vehicleUploading, setVehicleUploading] = useState(false);

    // Step 5 states
    const [vehicleFileList, setVehicleFileList] = useState<ImageUploadItem[]>([]);
    const [vehicleUploadInfo, setVehicleUploadInfo] = useState<UploadInfo | null>(null);
    const [vehiclePickerVisible, setVehiclePickerVisible] = useState(false);
    
    const [createAccountDeliveryDriver, { loading: creating }] = useMutation<
        CreateAccountDeliveryDriverResponse,
        CreateAccountDeliveryDriverVariables
    >(CREATE_ACCOUNT_DELIVERY_DRIVER_MUTATION);

    const { data: vehicleTypesData } = useQuery<GetAllVehicleTypesResponse>(
        GET_ALL_VEHICLE_TYPES,
        {
            fetchPolicy: 'cache-first'
        }
    );

    const [checkDspCodeExists] = useLazyQuery(CHECK_DSP_CODE_EXISTS);

    // 动态生成 vehicleTypes columns
    const vehicleTypes = [
        (vehicleTypesData?.getAllVehicleTypes || []).map(v => {
            let label = v.nameEn; // 默认英文
            if (i18n.language === 'zh' || i18n.language === 'zh-CN') {
                label = v.nameZh || v.nameEn;
            } else if (i18n.language === 'es' || i18n.language === 'es-ES') {
                label = v.nameEs || v.nameEn;
            }
            return {
                label,
                value: v.id,
                key: v.id
            };
        })
    ];

    interface UploadInfo {
        file: File;
        objectKey: string;
    }

    // 初始化表单数据
    useEffect(() => {
        form.setFieldsValue(formData);
        setSelectedCountryCode(formData.countryCode);
    }, []);

    // 保存当前步骤的数据
    const saveCurrentStepData = () => {
        const currentFormValues = form.getFieldsValue();
        setFormData(prev => ({
            ...prev,
            ...currentFormValues,
            countryCode: selectedCountryCode
        }));
    };

    // 恢复步骤数据
    const restoreStepData = (step: number) => {
        // 恢复表单数据
        form.setFieldsValue(formData);
        
        // 恢复特定状态
        setSelectedCountryCode(formData.countryCode);
    };

    // Validation logic for password（统一规则：8-16 位，含字母+数字+特殊字符，不分大小写）
    const { lengthOk: isLength, complexityOk: isComplex } = checkPassword(formData.password);

    // Avatar upload handler
    const handleAvatarUpload = async (file: File): Promise<ImageUploadItem> => {
        setAvatarUploading(true);
        try {
            const isImage = file.type.startsWith('image/');
            if (!isImage) {
                Toast.show('Only image files can be uploaded.');
                throw new Error('Invalid file type');
            }
            const isLt2M = file.size / 1024 / 1024 < 2;
            if (!isLt2M) {
                Toast.show('The image file size must be less than 2MB.');
                throw new Error('File too large');
            }

            const { objectKey } = await registrationFileUpload(file, registrationToken);
            setAvatarUploadInfo({ file, objectKey });
            const img = { url: URL.createObjectURL(file) };
            setAvatarFileList([img]);
            return img;
        } catch (error) {
            Toast.show('Upload Fail');
            throw error;
        }finally {
            setAvatarUploading(false);
        }
    };

    // Vehicle photo upload handler
    const handleVehicleUpload = async (file: File): Promise<ImageUploadItem> => {
        setVehicleUploading(true);
        try {
            const isImage = file.type.startsWith('image/');
            if (!isImage) {
                Toast.show(t('pages.delivery.driver.register.onlyImageFiles'));
                throw new Error('Invalid file type');
            }
            const isLt2M = file.size / 1024 / 1024 < 2;
            if (!isLt2M) {
                Toast.show(t('pages.delivery.driver.register.imageSizeLimit'));
                throw new Error('File too large');
            }
            const { objectKey } = await registrationFileUpload(file, registrationToken);
            setVehicleUploadInfo({ file, objectKey });
            const img = { url: URL.createObjectURL(file) };
            setVehicleFileList([img]);
            return img;
        } catch (error) {
            Toast.show(t('pages.delivery.driver.register.uploadFail'));
            throw error;
        }finally {
            setVehicleUploading(false);
        }
    };

    // Delete avatar
    const handleDeleteAvatar = () => {
        setAvatarFileList([]);
        setAvatarUploadInfo(null);
    };

    // 获取验证码
    const handleGetVerificationCode = async () => {
        await sendRegisterVerificationCode();
    };

    // Check if current step can proceed
    const canProceed = () => {
        switch (currentStep) {
            case 0: // DSP Code
                return !!formData.dspCode.trim();
            case 1: // Email/Phone
                return !!formData.phone && !!selectedCountryCode && (verificationCode.length === 6 || formData.verificationCode.length === 6);
            case 2: // Password
                return !!formData.password && !!formData.confirm;
            case 3: // Profile
                return !!formData.firstName && !!formData.lastName;
            case 4: // Driver Info
                return !!formData.vehicleTypeId && !!formData.vehiclePlate && vehicleFileList.length === 1;
            default:
                return false;
        }
    };

    // Handle next step
    const handleNext = async () => {
        // 保存当前步骤数据
        saveCurrentStepData();

        // DSP Code校验
        if (currentStep === 0) {
            if (!formData.dspCode.trim()) {
                Toast.show(t('login.dspCode.required'));
                return;
            }
            try {
                const { data } = await checkDspCodeExists({
                    variables: { dspCode: formData.dspCode }
                });
                if (!data?.checkDspCodeExists)  {
                    Toast.show(t('login.dspCode.invalid'));
                    return;
                }
            } catch (e) {
                Toast.show(t('login.network.error'));
                return;
            }
        }

        // 手机号/验证码校验（步骤2）
        if (currentStep === 1) {
            // 1. 校验手机号
            const phoneValidation = validatePhoneNumber(formData.phone, selectedCountryCode);
            if (!phoneValidation.isValid) {
                Toast.show(phoneValidation.errorMessage!);
                return;
            }

            // 2. 校验验证码（包括格式和后端校验）
            const codeValidation = await validateVerificationCode(
                Number(selectedCountryCode.replace('+', '')),
                phoneValidation.phoneDigits!,
                formData.verificationCode,
                {
                    type: 'register'
                }
            );

            if (!codeValidation.isValid) {
                if (codeValidation.errorMessage) {
                    Toast.show(codeValidation.errorMessage);
                }
                return;
            }
            setRegistrationToken(codeValidation.token ?? null);
            if (formData.email) {
                const email = formData.email.trim();
                if (!/^\S+@\S+\.\S+$/.test(email)) {
                    Toast.show(t('login.email.invalid'));
                    return;
                }
            }
        }

        if (currentStep === 2) {
            if (!formData.password) {
                Toast.show(t('login.password.required'));
                return;
            }
            if (!formData.confirm) {
                Toast.show(t('login.password.confirm'));
                return;
            }
            if (formData.password !== formData.confirm) {
                Toast.show(t('login.password.mismatch'));
                return;
            }
            if (!isLength) {
                Toast.show(t('login.password.length'));
                return;
            }
            if (!isComplex) {
                Toast.show(t('login.password.complexity'));
                return;
            }
        }

        if (currentStep === 4) {
            // Final submit
            await handleFinalSubmit();
        } else {
            const nextStep = currentStep + 1;
            setCurrentStep(nextStep);
            // 恢复下一步的数据
            setTimeout(() => restoreStepData(nextStep), 0);
        }
    };

    // Handle back
    const handleBack = () => {
        if (currentStep === 0) {
            navigate(-1);
        } else {
            // 保存当前步骤数据
            saveCurrentStepData();
            const prevStep = currentStep - 1;
            setCurrentStep(prevStep);
            // 恢复上一步的数据
            setTimeout(() => restoreStepData(prevStep), 0);
        }
    };

    // Final submission
    const handleFinalSubmit = async () => {
        // 最终提交前保存当前步骤数据
        saveCurrentStepData();
        const finalFormData = { ...formData, ...form.getFieldsValue() };

        if (!finalFormData.vehicleTypeId) {
            Toast.show(t('pages.delivery.register.selectVehicleType'));
            return;
        }
        if (!finalFormData.vehiclePlate) {
            Toast.show(t('pages.delivery.register.enterLicensePlate'));
            return;
        }
        if (vehicleFileList.length !== 1) {
            Toast.show(t('pages.delivery.register.uploadVehiclePhoto'));
            return;
        }
        if (!vehicleUploadInfo || !vehicleUploadInfo.objectKey) {
            Toast.show(t('pages.delivery.register.vehiclePhotoProcessing'));
            return;
        }

        try {
            const cleanVehicleType = finalFormData.vehicleTypeId;

            const input: CreateAccountDeliveryDriverInput = {
                email: finalFormData.email ? finalFormData.email : null,
                countryCode: Number(selectedCountryCode.replace('+', '')),
                phone: finalFormData.phone,
                password: finalFormData.password,
                name: (finalFormData.firstName || '') + (finalFormData.middleName || '') + (finalFormData.lastName || ''),
                vehicleTypeId: cleanVehicleType,
                vehiclePlate: finalFormData.vehiclePlate,
                avatarStorageToken: avatarUploadInfo?.objectKey || null,
                vehicleImageS3KeysJson: vehicleUploadInfo ? [vehicleUploadInfo.objectKey] : [],
                dspCode: finalFormData.dspCode,
                status: 1,
                isFromMobile: true,
                registrationToken: registrationToken
            };

            const cleanInput = Object.entries(input).reduce((acc, [key, value]) => {
                acc[key] = value === undefined ? null : value;
                return acc;
            }, {} as Record<string, any>);

            Toast.show(t('pages.delivery.register.submitting'));

            const { data, errors } = await createAccountDeliveryDriver({
                variables: { input: cleanInput as CreateAccountDeliveryDriverInput }
            });

            if (errors) {
                console.error('GraphQL错误:', errors);
                const error = errors[0];
                const messageKey = error?.extensions?.messageKey;
                
                if (messageKey) {
                    // 直接使用国际化消息，如果没有就显示原始错误
                    const message = t(`${messageKey}.message`) || error.message || 'Registration failed';
                    Toast.show(message);
                } else {
                    Toast.show(`fail to register: ${error?.message || 'unknown error'}`);
                }
                return;
            }

            Toast.show(t('pages.delivery.driver.register.success'));
            navigate('/login');

        } catch (error: any) {
            if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                const gqlError = error.graphQLErrors[0];
                const messageKey = gqlError.extensions?.messageKey;
                
                if (messageKey) {
                    // 直接使用国际化消息
                    const message = t(`${messageKey}.message`) || gqlError.message || 'Submission failed, please try again';
                    Toast.show(message);
                } else {
                    // 没有 messageKey，使用默认错误处理
                    let errorMessage = 'Submission failed, please try again';
                    if (gqlError.extensions?.code === 'INTERNAL_ERROR') {
                        errorMessage = 'Server internal error. Please contact the administrator.';
                    } else if (gqlError.extensions?.code === 'VALIDATION_ERROR') {
                        errorMessage = gqlError.message || 'Verification failed. Please check the input.';
                    } else {
                        errorMessage = gqlError.message || errorMessage;
                    }
                    Toast.show(errorMessage);
                }
            } else if (error.networkError) {
                Toast.show('Network connection error. Please check the network.');
            } else {
                Toast.show(error.message || 'Submission failed, please try again');
            }
        }
    };

    // Render step content
    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col items-center">
                            <h1 className="text-xl font-semibold">{t(`pages.login.registerPage1.enterDSPCode`)}</h1>
                            <p className="text-gray-500">{t(`pages.login.registerPage1.dspCodeDesc`)}</p>
                        </div>
                        <Form.Item name="dspCode">
                            <Input
                                clearable
                                value={formData.dspCode}
                                onChange={val => setFormData(prev => ({ ...prev, dspCode: val }))}
                                placeholder={t(`pages.login.dspCode`)}
                            />
                        </Form.Item>
                    </div>
                );

            case 1:
                return (
                    <div className="flex flex-col gap-4">
                        <div className="flex gap-2">
                            <Form.Item name="countryCode" className="mb-0 w-[70px]">
                                <div
                                    className="w-full h-full flex items-center justify-center gap-1 cursor-pointer"
                                    onClick={() => setPickerVisible(true)}
                                >
                                    <span className="text-lg">
                                        {countryCodes.find(c => c.value === selectedCountryCode)?.flag || '🇺🇸'}
                                    </span>
                                    <span>{selectedCountryCode}</span>
                                </div>
                            </Form.Item>
                            <Form.Item name="phone" className="mb-0 flex-1">
                                <Input
                                    clearable
                                    placeholder={t(`pages.login.phoneNumber`)}
                                    type="tel"
                                    inputMode="tel"
                                    value={formData.phone}
                                    onChange={val => {
                                        const digitsOnly = val.replace(/\D/g, '');
                                        const maxLength = selectedCountryCode === '+86' ? 11 : 10; // CN 11 digits, US 10 digits
                                        const limitedDigits = digitsOnly.slice(0, maxLength);
                                        setFormData(prev => ({ ...prev, phone: limitedDigits }));
                                        form.setFieldValue('phone', limitedDigits);
                                    }}
                                />
                            </Form.Item>
                        </div>
                        <Form.Item name="verificationCode" className="w-full">
                            <div className="adm-input">
                                <Input
                                    className="adm-input-element"
                                    placeholder={t('common.verificationCode.placeholder')}
                                    type="tel"
                                    inputMode="numeric"
                                    value={verificationCode}
                                    onChange={val => {
                                        const digitsOnly = val.replace(/\D/g, '').slice(0, 6);
                                        setVerificationCode(digitsOnly);
                                        setFormData(prev => ({ ...prev, verificationCode: digitsOnly }));
                                    }}
                                />
                                <div className="px-2 whitespace-nowrap flex-shrink-0">
                                    <Button
                                        size="small"
                                        color="primary"
                                        fill="none"
                                        disabled={countdown > 0}
                                        onClick={handleGetVerificationCode}
                                    >
                                        {countdown > 0 ? `${countdown}s` : t('pages.login.getCode')}
                                    </Button>
                                </div>
                            </div>
                        </Form.Item>
                        <Form.Item name="email" className="w-full">
                            <Input
                                clearable
                                placeholder={t(`pages.login.registerPage2.email`)}
                                value={formData.email}
                                onChange={val => setFormData(prev => ({ ...prev, email: val }))}
                            />
                        </Form.Item>
                        <Picker
                            columns={[countryCodes.map(code => ({
                                label: `${code.flag} ${code.label}`,
                                value: code.value
                            }))]}
                            visible={pickerVisible}
                            value={[selectedCountryCode]}
                            onClose={() => setPickerVisible(false)}
                            onConfirm={(val: PickerValue[]) => {
                                const newCode = String(val[0]);
                                setSelectedCountryCode(newCode);
                                setFormData(prev => ({ ...prev, countryCode: newCode }));
                                form.setFieldValue('countryCode', newCode);
                                setPickerVisible(false);
                            }}
                        />
                    </div>
                );

            case 2:
                return (
                    <div className="flex flex-col gap-4">
                        <Form.Item name="password" className="w-full">
                            <div className="adm-input">
                                <Input
                                    className="adm-input-element"
                                    placeholder={t(`pages.login.password`)}
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    onChange={val => setFormData(prev => ({ ...prev, password: val }))}
                                />
                                <div
                                    className="cursor-pointer px-2"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOutline /> : <EyeInvisibleOutline />}
                                </div>
                            </div>
                        </Form.Item>
                        
                        <Form.Item>
                            <div className="flex items-start gap-2">
                                <div className="mt-[3px]">
                                    <Checkbox checked={isLength} />
                                </div>
                                <span className="text-[14px] text-[#888] leading-[1.6] -mt-[-2px]">
                                    {t(`pages.login.registerPage3.ps1`)}
                                </span>
                            </div>
                            <div className="flex items-start gap-2">
                                <div className="mt-[3px]">
                                    <Checkbox checked={isComplex} />
                                </div>
                                <span className="text-[14px] text-[#888] leading-[1.6] -mt-[-2px]">
                                    {t(`pages.login.registerPage3.ps2`)}
                                </span>
                            </div>
                        </Form.Item>

                        <Form.Item name="confirm" className="w-full">
                            <div className="adm-input">
                                <Input
                                    className="adm-input-element"
                                    placeholder={t(`pages.login.rePassword`)}
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={formData.confirm}
                                    onChange={val => setFormData(prev => ({ ...prev, confirm: val }))}
                                />
                                <div
                                    className="cursor-pointer px-2"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? <EyeOutline /> : <EyeInvisibleOutline />}
                                </div>
                            </div>
                        </Form.Item>
                    </div>
                );

            case 3:
                return (
                    <div className="flex flex-col gap-4">
                        <div className="flex items-center justify-center">
                            <div className="w-20 h-20 rounded-full border-2 border-[#E5E6EB] bg-[#F5F6FA] flex items-center justify-center relative overflow-hidden mt-2">
                                {avatarUploading ? (
                                    <SpinLoading color="primary"  className="text-[32px]" />
                                ) : avatarFileList.length === 0 ? (
                                    <PhotoSelector
                                        onPhotoSelected={handleAvatarUpload}
                                        buttonText={
                                            <div className="w-full h-full flex items-center justify-center">
                                                <UserOutline className="text-[40px] text-[#C7C3F4]" />
                                                <div className="absolute right-0 bottom-0 w-7 h-7 bg-white rounded-full border border-[#E5E6EB] flex items-center justify-center text-base text-[#C7C3F4]">
                                                    <AddOutline />
                                                </div>
                                            </div>
                                        }
                                        buttonProps={{
                                            style: { width: '100%', height: '100%' }
                                        }}
                                    />
                                ) : (
                                    <>
                                        <img
                                            src={avatarFileList[0].url}
                                            alt="avatar"
                                            className="w-full h-full object-cover rounded-full"
                                        />
                                        <div
                                            onClick={handleDeleteAvatar}
                                            className="absolute top-0 right-0 w-7 h-7 bg-white rounded-full border border-[#E5E6EB] flex items-center justify-center text-base text-[#C7C3F4] cursor-pointer select-none"
                                        >
                                            <CloseOutline />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <Form.Item name="firstName" className="w-full">
                            <Input 
                                clearable 
                                placeholder={t(`pages.login.registerPage4.firstName`)}
                                value={formData.firstName}
                                onChange={val => setFormData(prev => ({ ...prev, firstName: val }))}
                            />
                        </Form.Item>
                        <Form.Item name="middleName" className="w-full">
                            <Input 
                                clearable 
                                placeholder={t(`pages.login.registerPage4.middleName`)}
                                value={formData.middleName}
                                onChange={val => setFormData(prev => ({ ...prev, middleName: val }))}
                            />
                        </Form.Item>
                        <Form.Item name="lastName" className="w-full">
                            <Input 
                                clearable 
                                placeholder={t(`pages.login.registerPage4.lastName`)}
                                value={formData.lastName}
                                onChange={val => setFormData(prev => ({ ...prev, lastName: val }))}
                            />
                        </Form.Item>
                    </div>
                );

            case 4:
                return (
                    <div className="flex flex-col gap-4">
                        <Form.Item name="vehiclePlate" className="mb-1">
                            <Input 
                                clearable 
                                placeholder={t(`pages.login.registerPage5.vehiclePlate`)}
                                value={formData.vehiclePlate}
                                onChange={val => setFormData(prev => ({ ...prev, vehiclePlate: val }))}
                            />
                        </Form.Item>

                        <Form.Item name="vehicleTypeId" className="mb-1">
                            <div
                                className="h-[52px] flex items-center justify-between"
                                onClick={() => setVehiclePickerVisible(true)}
                            >
                                <span className={formData.vehicleTypeId ? 'text-[#222]' : 'text-[#999]'}>
                                    { vehicleTypesData?.getAllVehicleTypes.find(v => v.id === formData.vehicleTypeId)
                                        ? (() => {
                                            const vehicleType = vehicleTypesData?.getAllVehicleTypes.find(v => v.id === formData.vehicleTypeId);
                                            if (i18n.language === 'zh' || i18n.language === 'zh-CN') {
                                                return vehicleType?.nameZh || vehicleType?.nameEn;
                                            } else if (i18n.language === 'es' || i18n.language === 'es-ES') {
                                                return vehicleType?.nameEs || vehicleType?.nameEn;
                                            } else {
                                                return vehicleType?.nameEn;
                                            }
                                        })()
                                        : t(`pages.login.registerPage5.vehicleType`) }
                                </span>
                                <span className="text-[#999]">›</span>
                            </div>
                        </Form.Item>

                        <Picker
                            columns={vehicleTypes}
                            visible={vehiclePickerVisible}
                            onClose={() => setVehiclePickerVisible(false)}
                            onConfirm={(val) => {
                                const vehicleTypeValue = String(val[0]);
                                setFormData(prev => ({ ...prev, vehicleTypeId: vehicleTypeValue }));
                                form.setFieldValue('vehicleTypeId', vehicleTypeValue);
                                setVehiclePickerVisible(false);
                            }}
                        />

                        <Form.Item>
                            <div className="flex flex-col items-start">
                                <span className="text-[16px] font-medium text-[#222] mb-1">
                                    {t(`pages.login.registerPage5.vehiclePhoto`)}
                                </span>
                                <span className="text-[12px] text-[#999]">
                                    {t(`pages.login.registerPage5.vehiclePhotoDesc`)}
                                </span>
                            </div>
                        </Form.Item>

                        <Form.Item>
                            <div className="w-full h-[200px] flex items-center justify-center rounded-xl bg-[#F5F6FA] border border-[#E5E6EB]">
                                {vehicleUploading ? (
                                    <SpinLoading color="primary" className="text-[40px]"  />
                                ) : vehicleFileList.length < 1 ? (
                                    <PhotoSelector
                                        onPhotoSelected={handleVehicleUpload}
                                        buttonText={
                                            <div className="w-full h-[200px] flex items-center justify-center">
                                                <CameraOutline className="text-[100px] text-[#C7C3F4]" />
                                            </div>
                                        }
                                        buttonProps={{
                                            style: { width: '100%', height: '100%' }
                                        }}
                                    />
                                ) : (
                                    <div className="w-full h-[200px]">
                                        <img
                                            src={vehicleFileList[0].url}
                                            alt="vehicle"
                                            className="w-full h-full object-cover rounded-xl"
                                        />
                                        <div
                                            onClick={() => {
                                                setVehicleFileList([]);
                                                setVehicleUploadInfo(null);
                                            }}
                                            className="absolute top-4 right-4 w-7 h-7 bg-white rounded-full border border-[#E5E6EB] flex items-center justify-center text-base text-[#C7C3F4] cursor-pointer select-none"
                                        >
                                            <CloseOutline />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Form.Item>
                    </div>
                );

            default:
                return null;
        }
    };

    const stepTitles = [
        t('pages.login.dspCode'),
        t('pages.login.contactInfo'),
        t('pages.login.password'),
        t('pages.login.profile'),
        t('pages.login.driverInfo')
    ];

    return (
        <div className="min-h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={handleBack} className="text-black">
                    {t(`pages.login.registerDriver`)}
                </NavBar>
            </div>
            
            <div className="p-4">
                <Steps current={currentStep} className="mb-6">
                    {stepTitles.map((title, index) => (
                        <Step key={title} title={title} />
                    ))}
                </Steps>

                <Form
                    form={form}
                    layout="vertical"
                    style={{
                        '--border-top': 'none',
                        '--border-inner': 'none',
                        '--border-bottom': 'none',
                    }}
                >
                    {renderStepContent()}
                </Form>

                <div className="flex justify-center mt-8">
                    <Button
                        block
                        color="primary"
                        size="large"
                        disabled={!canProceed() || creating}
                        onClick={handleNext}
                        loading={creating}
                    >
                        {currentStep === 4 ? t('pages.login.submitButton') : t('pages.login.continueButton')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default UnifiedRegister; 